// Position represents the TOP-LEFT corner of the overlay (fraction of viewport).
// Default y=0.72 places the overlay near the bottom (visually equivalent to
// the former y=0.84 with a center-anchor).
export const DEFAULT_CAPTION_OVERLAY_POSITION = Object.freeze({ x: 0.02, y: 0.72 });
export const DEFAULT_CAPTION_OVERLAY_MARGIN_PX = 12;
export const CAPTION_OVERLAY_X_PREFERENCE_KEY = "captionOverlayPositionX";
export const CAPTION_OVERLAY_Y_PREFERENCE_KEY = "captionOverlayPositionY";

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function isFiniteNumber(value) {
  return typeof value === "number" && Number.isFinite(value);
}

function normalizeFallback(fallback) {
  const x = isFiniteNumber(fallback?.x) ? clamp(fallback.x, 0, 1) : DEFAULT_CAPTION_OVERLAY_POSITION.x;
  const y = isFiniteNumber(fallback?.y) ? clamp(fallback.y, 0, 1) : DEFAULT_CAPTION_OVERLAY_POSITION.y;

  return { x, y };
}

function normalizeBounds(bounds) {
  const minX = isFiniteNumber(bounds?.minX) ? clamp(bounds.minX, 0, 1) : 0;
  const maxX = isFiniteNumber(bounds?.maxX) ? clamp(bounds.maxX, minX, 1) : 1;
  const minY = isFiniteNumber(bounds?.minY) ? clamp(bounds.minY, 0, 1) : 0;
  const maxY = isFiniteNumber(bounds?.maxY) ? clamp(bounds.maxY, minY, 1) : 1;

  return { minX, maxX, minY, maxY };
}

export function normalizeCaptionOverlayPosition(position, fallback = DEFAULT_CAPTION_OVERLAY_POSITION) {
  const normalizedFallback = normalizeFallback(fallback);

  const x = isFiniteNumber(position?.x) ? clamp(position.x, 0, 1) : normalizedFallback.x;
  const y = isFiniteNumber(position?.y) ? clamp(position.y, 0, 1) : normalizedFallback.y;

  return { x, y };
}

export function computeCaptionOverlayPositionBounds({
  viewportWidth,
  viewportHeight,
  overlayWidth,
  overlayHeight,
  marginPx = DEFAULT_CAPTION_OVERLAY_MARGIN_PX
}) {
  const safeViewportWidth = isFiniteNumber(viewportWidth) && viewportWidth > 0 ? viewportWidth : 1;
  const safeViewportHeight = isFiniteNumber(viewportHeight) && viewportHeight > 0 ? viewportHeight : 1;
  const safeOverlayWidth = isFiniteNumber(overlayWidth) && overlayWidth > 0 ? overlayWidth : 0;
  const safeOverlayHeight = isFiniteNumber(overlayHeight) && overlayHeight > 0 ? overlayHeight : 0;
  const safeMarginPx = isFiniteNumber(marginPx) ? Math.max(0, marginPx) : DEFAULT_CAPTION_OVERLAY_MARGIN_PX;

  // With a top-left anchor, the minimum position is just the margin,
  // and the maximum is the viewport minus the full overlay size plus margin.
  const minX = clamp(safeMarginPx / safeViewportWidth, 0, 1);
  const minY = clamp(safeMarginPx / safeViewportHeight, 0, 1);
  const maxX = clamp(1 - (safeOverlayWidth + safeMarginPx) / safeViewportWidth, minX, 1);
  const maxY = clamp(1 - (safeOverlayHeight + safeMarginPx) / safeViewportHeight, minY, 1);

  return { minX, maxX, minY, maxY };
}

export function clampCaptionOverlayPosition(position, bounds) {
  const normalized = normalizeCaptionOverlayPosition(position);
  const normalizedBounds = normalizeBounds(bounds);

  return {
    x: clamp(normalized.x, normalizedBounds.minX, normalizedBounds.maxX),
    y: clamp(normalized.y, normalizedBounds.minY, normalizedBounds.maxY)
  };
}

export function parseStoredCaptionOverlayPosition(rawValue, fallback = DEFAULT_CAPTION_OVERLAY_POSITION) {
  const normalizedFallback = normalizeFallback(fallback);

  if (typeof rawValue !== "string" || rawValue.trim().length === 0) {
    return normalizedFallback;
  }

  try {
    const parsed = JSON.parse(rawValue);
    return normalizeCaptionOverlayPosition(parsed, normalizedFallback);
  } catch (error) {
    return normalizedFallback;
  }
}

export function resolveCaptionOverlayPositionFromPreferences(preferences, fallback = DEFAULT_CAPTION_OVERLAY_POSITION) {
  return normalizeCaptionOverlayPosition(
    {
      x: preferences?.[CAPTION_OVERLAY_X_PREFERENCE_KEY],
      y: preferences?.[CAPTION_OVERLAY_Y_PREFERENCE_KEY]
    },
    fallback
  );
}

export function buildCaptionOverlayPositionPreferences(position, fallback = DEFAULT_CAPTION_OVERLAY_POSITION) {
  const normalized = normalizeCaptionOverlayPosition(position, fallback);

  return {
    [CAPTION_OVERLAY_X_PREFERENCE_KEY]: normalized.x,
    [CAPTION_OVERLAY_Y_PREFERENCE_KEY]: normalized.y
  };
}
